import { useQuery } from '@tanstack/react-query'
import { orientationAPI } from '@/lib/orientationApi'
import type { OrientationHistoryItem } from '@/lib/orientationApi'
import { useState } from 'react'

const statusConfig: Record<string, { label: string; className: string }> = {
  SCORED:    { label: 'Évalué',    className: 'bg-blue-100 text-blue-800' },
  CONFIRMED: { label: 'Confirmé',  className: 'bg-success-100 text-success-800' },
  CREATED:   { label: 'En attente', className: 'bg-neutral-100 text-neutral-600' },
  CANCELLED: { label: 'Annulé',   className: 'bg-red-100 text-red-700' },
}

const graviteConfig: Record<string, { label: string; className: string }> = {
  LEGERE:   { label: 'Légère',   className: 'bg-blue-50 text-blue-700' },
  MODEREE:  { label: 'Modérée',  className: 'bg-yellow-50 text-yellow-700' },
  GRAVE:    { label: 'Grave',    className: 'bg-orange-50 text-orange-700' },
  CRITIQUE: { label: 'Critique', className: 'bg-red-50 text-red-700' },
}

export default function HistoryPage() {
  const [expanded, setExpanded] = useState<string | null>(null)

  const { data: history, isLoading, error } = useQuery<OrientationHistoryItem[]>({
    queryKey: ['orientation-history'],
    queryFn: orientationAPI.getHistory,
    refetchInterval: 30000,
  })

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-neutral-500">Chargement de l'historique...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-800 text-sm">Erreur lors du chargement de l'historique</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-neutral-900">Historique des orientations</h1>
          <p className="text-sm text-neutral-500">{history?.length || 0} orientation(s)</p>
        </div>
      </div>

      {(!history || history.length === 0) ? (
        <div className="text-center py-12 text-neutral-500">
          <p className="text-lg">Aucune orientation</p>
          <p className="text-sm mt-1">Les orientations effectuees apparaitront ici</p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <div
              key={item.orientationRef}
              className="bg-white rounded-xl border border-neutral-200 overflow-hidden"
            >
              <button
                onClick={() => setExpanded(expanded === item.orientationRef ? null : item.orientationRef)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-primary-600 font-medium">{item.orientationRef}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[item.status]?.className || 'bg-neutral-100 text-neutral-600'}`}>
                    {statusConfig[item.status]?.label ?? item.status}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${graviteConfig[item.gravite]?.className || ''}`}>
                    {graviteConfig[item.gravite]?.label ?? item.gravite}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-neutral-400">
                    {item.createdAt ? new Date(item.createdAt).toLocaleString('fr-FR') : ''}
                  </span>
                  <svg
                    className={`w-4 h-4 text-neutral-400 transform transition-transform ${expanded === item.orientationRef ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {expanded === item.orientationRef && (
                <div className="px-4 pb-4 border-t border-neutral-100 pt-3 space-y-2 text-sm">
                  <div className="flex gap-4">
                    <span className="text-neutral-500 w-32">Motif:</span>
                    <span className="text-neutral-900">{item.motif}</span>
                  </div>
                  <div className="flex gap-4">
                    <span className="text-neutral-500 w-32">Profil scoring:</span>
                    <span className="text-neutral-900">{item.scoringProfile}</span>
                  </div>
                  {item.chosenStructureName && (
                    <div className="flex gap-4">
                      <span className="text-neutral-500 w-32">Structure choisie:</span>
                      <span className="text-neutral-900 font-medium">{item.chosenStructureName}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
